import { Box, Menu, MenuItem } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { OverlayLabel } from './OverlayLabel';

export interface OverlayMenuOption<T> {
    value: T;
    label: string;
    icon?: React.ReactNode;
}

interface OverlayMenuProps<T> {
    options: OverlayMenuOption<T>[];
    value: T;
    onChange: (value: T) => void;
    renderLabel?: (option: OverlayMenuOption<T> | undefined) => React.ReactNode;
    icon?: React.ReactNode;
    'data-testid'?: string;
}

export function OverlayMenu<T>({
    options,
    value,
    onChange,
    renderLabel,
    icon,
    'data-testid': testId,
}: OverlayMenuProps<T>) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const currentOption = options.find((o) => o.value === value);

    const handleSelect = (optionValue: T) => {
        onChange(optionValue);
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                gap={0.25}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                data-testid={testId}
                sx={{
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                }}
            >
                {icon}
                <OverlayLabel sx={{ whiteSpace: 'nowrap' }}>
                    {renderLabel
                        ? renderLabel(currentOption)
                        : (currentOption?.label ?? 'Select')}
                </OverlayLabel>
                <ChevronDown size={9} style={{ marginTop: 2 }} />
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.85)',
                        },
                    },
                }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={String(option.value)}
                        selected={value === option.value}
                        onClick={() => handleSelect(option.value)}
                        sx={{ fontSize: 11, color: 'white' }}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            {option.icon}
                            {option.label}
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
